// src/hooks/useTenantConfigurations.ts
// ✅ LOGGING: Complete migration to centralized logging system

import { useState, useEffect, useCallback, useMemo } from 'react';
import { tenantConfigCache } from '../services/tenantConfigCache';
import { logger } from '../utils/logger';

// ✅ LOGGING: Create dedicated logger for Tenant Configurations hook
const tenantConfigLogger = logger.createServiceLogger('TenantConfig');

interface TenantConfigValues {
  ranks: string[];
  assignments: string[];
  departments: string[];
  shifts: string[];
  inventoryCategories: string[];
  inventoryLocations: string[];
  assignmentReasons: string[];
}

interface UseTenantConfigurationsResult {
  configs: TenantConfigValues;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  cacheStats: { size: number };
}

// Default fallback values (used only if API fails)
const DEFAULT_CONFIGS: TenantConfigValues = {
  ranks: [
    'Officer',
    'Senior Officer',
    'Corporal',
    'Sergeant',
    'Lieutenant',
    'Captain',
    'Major',
    'Chief'
  ],
  assignments: [
    'Patrol',
    'Traffic',
    'Investigations',
    'K9',
    'SWAT',
    'Community Relations',
    'Administration',
    'Training',
    'Evidence',
    'Dispatch'
  ],
  departments: [
    'Patrol Division',
    'Investigations Division',
    'Administrative Division',
    'Special Operations',
    'Support Services'
  ],
  shifts: [
    'Day Shift',
    'Evening Shift',
    'Night Shift',
    'Rotating'
  ],
  inventoryCategories: [
    'Firearms',
    'Electronics',
    'Protective Gear',
    'Vehicles',
    'Medical',
    'Communications',
    'Tools',
    'Uniforms',
    'Training Equipment'
  ],
  inventoryLocations: [
    'Armory',
    'Equipment Room',
    'Supply Room',
    'Communications Room',
    'Vehicle Bay',
    'Training Room',
    'Evidence Room',
    'Admin Office',
    'Storage Unit'
  ],
  assignmentReasons: [
    'Initial Issue',
    'Replacement-Lost',
    'Replacement-Damaged',
    'Promotion',
    'Transfer',
    'Temporary',
    'Training'
  ]
};

// ✅ FIX: Helper function to safely check array length
const safeArrayLength = (arr: any): number => {
  return Array.isArray(arr) ? arr.length : 0;
};

// ✅ FIX: Helper function to safely get array value or default
const safeArrayValue = (arr: any, defaultValue: string[]): string[] => {
  return Array.isArray(arr) && arr.length > 0 ? arr : defaultValue;
};

// Global cache to prevent duplicate calls during React StrictMode
let globalLoadingPromise: Promise<any> | null = null;
let globalLoadingKey = '';

export const useTenantConfigurations = (operatingTenantId?: string): UseTenantConfigurationsResult => {
  const [configs, setConfigs] = useState<TenantConfigValues>(DEFAULT_CONFIGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize tenant ID to prevent unnecessary effect runs
  const memoizedTenantId = useMemo(() => {
    return operatingTenantId;
  }, [operatingTenantId]);

  const loadConfigurations = useCallback(async () => {
    const timer = logger.startTimer('TenantConfig', 'loadConfigurations');
    const currentKey = `configs-${memoizedTenantId}`;
    
    // ✅ FIX: Prevent duplicate calls during React StrictMode
    if (globalLoadingPromise && globalLoadingKey === currentKey) {
      tenantConfigLogger.debug('Deduplicating configuration load request', {
        operatingTenantId: memoizedTenantId,
        reason: 'Already loading'
      });
      return globalLoadingPromise;
    }

    setLoading(true);
    setError(null);

    const loadingPromise = (async () => {
      try {
        tenantConfigLogger.info('Starting optimized tenant configurations load', {
          operatingTenantId: memoizedTenantId,
          timestamp: new Date().toISOString()
        });

        // Use the cache for efficient batch loading
        const loadedConfigs = await tenantConfigCache.getAllConfigurations(memoizedTenantId);

        // ✅ FIX: Apply defaults for any null/empty arrays using safe helpers
        const finalConfigs: TenantConfigValues = {
          ranks: safeArrayValue(loadedConfigs.ranks, DEFAULT_CONFIGS.ranks),
          assignments: safeArrayValue(loadedConfigs.assignments, DEFAULT_CONFIGS.assignments),
          departments: safeArrayValue(loadedConfigs.departments, DEFAULT_CONFIGS.departments),
          shifts: safeArrayValue(loadedConfigs.shifts, DEFAULT_CONFIGS.shifts),
          inventoryCategories: safeArrayValue(loadedConfigs.inventoryCategories, DEFAULT_CONFIGS.inventoryCategories),
          inventoryLocations: safeArrayValue(loadedConfigs.inventoryLocations, DEFAULT_CONFIGS.inventoryLocations),
          assignmentReasons: safeArrayValue(loadedConfigs.assignmentReasons, DEFAULT_CONFIGS.assignmentReasons)
        };

        setConfigs(finalConfigs);

        // Log configuration status using safe length check
        const configStatus = {
          ranks: safeArrayLength(loadedConfigs.ranks) > 0 ? 'loaded' : 'default',
          assignments: safeArrayLength(loadedConfigs.assignments) > 0 ? 'loaded' : 'default',
          departments: safeArrayLength(loadedConfigs.departments) > 0 ? 'loaded' : 'default',
          shifts: safeArrayLength(loadedConfigs.shifts) > 0 ? 'loaded' : 'default',
          categories: safeArrayLength(loadedConfigs.inventoryCategories) > 0 ? 'loaded' : 'default',
          locations: safeArrayLength(loadedConfigs.inventoryLocations) > 0 ? 'loaded' : 'default',
          reasons: safeArrayLength(loadedConfigs.assignmentReasons) > 0 ? 'loaded' : 'default'
        };

        const loadedCount = Object.values(configStatus).filter(status => status === 'loaded').length;

        tenantConfigLogger.info('Optimized tenant configurations loaded successfully', {
          operatingTenantId: memoizedTenantId,
          loadedCount,
          totalConfigs: 7,
          configTypes: configStatus,
          cacheStats: tenantConfigCache.getCacheStats()
        });

        // Only set error if ALL requests failed
        if (loadedCount === 0) {
          const errorMessage = 'Failed to load any tenant configurations. Using system defaults.';
          setError(errorMessage);
          tenantConfigLogger.warn('All configuration requests failed, using defaults', {
            operatingTenantId: memoizedTenantId
          });
        }

      } catch (err: any) {
        const errorMessage = 'Failed to load tenant configurations. Using system defaults.';
        tenantConfigLogger.error('Error loading tenant configurations', {
          operatingTenantId: memoizedTenantId,
          error: err.message,
          stack: err.stack
        }, err);
        setError(errorMessage);
        // Keep default configs on error
      } finally {
        setLoading(false);
        timer(); // Complete timing
        // Clear the global loading state
        if (globalLoadingKey === currentKey) {
          globalLoadingPromise = null;
          globalLoadingKey = '';
        }
      }
    })();

    // Set global loading state
    globalLoadingPromise = loadingPromise;
    globalLoadingKey = currentKey;

    return loadingPromise;
  }, [memoizedTenantId]);

  // Load configurations on mount and when tenant ID changes
  useEffect(() => {
    tenantConfigLogger.debug('Effect triggered for tenant configurations', {
      operatingTenantId: memoizedTenantId
    });
    
    loadConfigurations();
  }, [loadConfigurations]);

  // Get cache stats for debugging
  const cacheStats = useMemo(() => tenantConfigCache.getCacheStats(), [configs]);

  return {
    configs,
    loading,
    error,
    refresh: loadConfigurations,
    cacheStats
  };
};

// Optimized convenience hooks with memoization
export const useUserRanks = (operatingTenantId?: string): string[] => {
  const { configs } = useTenantConfigurations(operatingTenantId);
  return useMemo(() => configs.ranks, [configs.ranks]);
};

export const useUserAssignments = (operatingTenantId?: string): string[] => {
  const { configs } = useTenantConfigurations(operatingTenantId);
  return useMemo(() => configs.assignments, [configs.assignments]);
};

export const useUserDepartments = (operatingTenantId?: string): string[] => {
  const { configs } = useTenantConfigurations(operatingTenantId);
  return useMemo(() => configs.departments, [configs.departments]);
};

export const useUserShifts = (operatingTenantId?: string): string[] => {
  const { configs } = useTenantConfigurations(operatingTenantId);
  return useMemo(() => configs.shifts, [configs.shifts]);
};

// Hook for preloading configurations (useful for critical user paths)
export const usePreloadTenantConfigurations = (operatingTenantId?: string) => {
  const memoizedTenantId = useMemo(() => operatingTenantId, [operatingTenantId]);

  useEffect(() => {
    if (memoizedTenantId) {
      tenantConfigLogger.debug('Preloading configurations for tenant', {
        operatingTenantId: memoizedTenantId
      });
      tenantConfigCache.preloadConfigurations(memoizedTenantId);
    }
  }, [memoizedTenantId]);
};

// Hook for cache management
export const useTenantConfigCache = () => {
  return useMemo(() => ({
    invalidateConfig: tenantConfigCache.invalidateConfig.bind(tenantConfigCache),
    invalidateAll: tenantConfigCache.invalidateAll.bind(tenantConfigCache),
    getCacheStats: tenantConfigCache.getCacheStats.bind(tenantConfigCache),
    preload: tenantConfigCache.preloadConfigurations.bind(tenantConfigCache)
  }), []);
};
